import { useEffect, useState, useCallback, useMemo } from "react";
import VaultLock from "@/components/VaultLock";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useI18n } from "@/i18n";
import PageHeader, { EmptyState } from "@/components/PageHeader";
import Modal from "@/components/Modal";
import MediaInput from "@/components/MediaInput";
import MediaViewer from "@/components/MediaViewer";
import HeirAssignmentPicker from "@/components/HeirAssignmentPicker";
import ExampleHint from "@/components/ExampleHint";
import {
  Plus,
  Lock,
  FileText,
  Image,
  Bitcoin,
  Briefcase,
  Eye,
  EyeOff,
  Copy,
  Pencil,
  Trash2,
  ExternalLink,
  Bomb,
} from "lucide-react";

const CAT_ICONS = {
  password: Lock,
  document: FileText,
  photo: Image,
  crypto: Bitcoin,
  asset: Briefcase,
};

const EMPTY = {
  category: "password",
  title: "",
  content: "",
  notes: "",
  username: "",
  url: "",
  media_type: "text",
  media_data: "",
  media_filename: "",
  media_size: 0,
  assigned_heirs: [],
  is_capsule: false,
};

export default function Vault() {
  const { t } = useI18n();
  // Privacy: never record session-replay on vault page (seed phrases, passwords).
  useEffect(() => {
    try { window.__ksTrackingPause && window.__ksTrackingPause(); } catch {}
    return () => { try { window.__ksTrackingResume && window.__ksTrackingResume(); } catch {} };
  }, []);
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [viewing, setViewing] = useState(null); // selected item to view
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [reveal, setReveal] = useState(false);
  // Randomised name-suffix so Chrome/Safari can't tie this form to any
  // known login-form heuristic and pop the Save-password / autofill prompt.
  // Combined with autocomplete="off" + data-form-type="other" + the
  // password-manager opt-out hints on every input, this is the strongest
  // browser-side mitigation we can apply without disabling the field entirely.
  const vaultFieldKey = useMemo(
    () => Math.random().toString(36).slice(2, 10),
    []
  );

  const load = useCallback(async () => {
    const r = await api.get("/vault");
    setItems(r.data);
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const openCreate = () => {
    setEditingId(null);
    setForm(EMPTY);
    setOpen(true);
  };

  const openEdit = (item) => {
    setEditingId(item.item_id);
    setForm({
      category: item.category,
      title: item.title,
      content: item.content || "",
      notes: item.notes || "",
      username: item.username || "",
      url: item.url || "",
      media_type: item.media_type || "text",
      media_data: item.media_data || "",
      media_filename: item.media_filename || "",
      media_size: item.media_size || 0,
      assigned_heirs: item.assigned_heirs || [],
      is_capsule: !!item.is_capsule,
    });
    setViewing(null);
    setOpen(true);
  };

  const save = async (e) => {
    e.preventDefault();
    try {
      if (editingId) {
        const r = await api.put(`/vault/${editingId}`, form);
        toast.success(t("saved"));
        setItems((arr) => arr.map((it) => (it.item_id === editingId ? r.data : it)));
      } else {
        const r = await api.post("/vault", form);
        toast.success(t("saved"));
        setItems((arr) => [...arr, r.data]);
      }
      setOpen(false);
      setEditingId(null);
      setForm(EMPTY);
    } catch {
      toast.error("Error");
    }
  };

  const remove = async (id) => {
    if (!window.confirm(t("confirm_delete"))) return;
    await api.delete(`/vault/${id}`);
    toast.success(t("deleted"));
    setItems((arr) => arr.filter((it) => it.item_id !== id));
    if (viewing?.item_id === id) setViewing(null);
  };

  const copy = async (text, label) => {
    try {
      await navigator.clipboard.writeText(text || "");
      toast.success(`${label}: ${t("copied")}`);
    } catch {
      toast.error("Error");
    }
  };

  return (
    <VaultLock>
    <div data-testid="vault-page">
      <PageHeader
        title={t("vault_title")}
        subtitle={t("vault_subtitle")}
        action={
          <button className="btn-gold" onClick={openCreate} data-testid="vault-add-btn">
            <Plus className="w-4 h-4" /> {t("vault_add")}
          </button>
        }
      />

      {items.length === 0 ? (
        <EmptyState icon={Lock}>{t("vault_empty")}</EmptyState>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {items.map((it) => {
            const Icon = CAT_ICONS[it.category] || Lock;
            return (
              <button
                key={it.item_id}
                onClick={() => {
                  setViewing(it);
                  setReveal(false);
                }}
                className="glass-card rounded-md p-5 text-left hover:border-gold transition group"
                data-testid={`vault-item-${it.item_id}`}
              >
                <div className="flex items-center gap-4">
                  <div className="p-3 rounded-md bg-gold/10 border border-gold-subtle relative">
                    <Icon className="w-5 h-5 text-gold" strokeWidth={1.4} />
                    {it.is_capsule && (
                      <span
                        className="absolute -top-1.5 -right-1.5 bg-obsidian border border-gold rounded-full p-0.5"
                        title={t("vault_capsule_tooltip")}
                      >
                        <Bomb className="w-2.5 h-2.5 text-gold" />
                      </span>
                    )}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="text-gold-light truncate flex items-center gap-2">
                      {it.title}
                      {it.is_capsule && (
                        <span className="text-[9px] uppercase tracking-wider bg-gold/15 text-gold px-1.5 py-0.5 rounded">
                          {t("vault_capsule_badge")}
                        </span>
                      )}
                    </div>
                    <div className="font-label text-faded mt-1">
                      {t(`cat_${it.category}`)}
                      {it.username ? ` · ${it.username}` : ""}
                    </div>
                  </div>
                  <Eye className="w-4 h-4 text-faded group-hover:text-gold transition shrink-0" />
                </div>
              </button>
            );
          })}
        </div>
      )}

      <ExampleHint slug="vault" testid="vault-hint" />

      {/* VIEW modal */}
      <Modal
        open={!!viewing && !open}
        onClose={() => setViewing(null)}
        title={viewing?.title || ""}
        testid="vault-view-modal"
      >
        {viewing && (
          <div className="space-y-5">
            <div className="font-label text-gold">{t(`cat_${viewing.category}`)}</div>

            {viewing.media_data && <MediaViewer media={viewing} />}

            {viewing.username && (
              <DetailField
                label={t("username")}
                value={viewing.username}
                onCopy={() => copy(viewing.username, t("username"))}
              />
            )}

            <div>
              <div className="font-label text-faded mb-2 ks-readonly-label">
                <Lock className="w-3 h-3 opacity-60" /> {t("content")}
              </div>
              <div className="flex items-center gap-2">
                <input
                  type={reveal ? "text" : "password"}
                  readOnly
                  value={viewing.content}
                  className="ks-readonly flex-1 font-mono outline-none focus:outline-none"
                  data-testid="vault-view-content"
                />
                <button
                  onClick={() => setReveal((v) => !v)}
                  className="btn-ghost px-3"
                  type="button"
                  data-testid="vault-reveal-btn"
                >
                  {reveal ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
                <button
                  onClick={() => copy(viewing.content, t("content"))}
                  className="btn-ghost px-3"
                  type="button"
                  data-testid="vault-copy-content"
                >
                  <Copy className="w-4 h-4" />
                </button>
              </div>
            </div>

            {viewing.url && (
              <div>
                <div className="font-label text-faded mb-2">{t("url")}</div>
                <a
                  href={viewing.url.startsWith("http") ? viewing.url : `https://${viewing.url}`}
                  target="_blank"
                  rel="noreferrer"
                  className="text-gold hover:text-gold-light flex items-center gap-2 break-all"
                >
                  {viewing.url} <ExternalLink className="w-3.5 h-3.5 shrink-0" />
                </a>
              </div>
            )}

            {viewing.notes && (
              <div>
                <div className="font-label text-faded mb-2">{t("notes")}</div>
                <div className="text-muted whitespace-pre-wrap text-sm">{viewing.notes}</div>
              </div>
            )}

            <div className="text-xs text-faded">
              {t("created_at")}: {new Date(viewing.created_at).toLocaleString()}
            </div>

            <div className="flex gap-3 justify-end pt-3 border-t border-gold-subtle">
              <button
                className="btn-ghost"
                onClick={() => remove(viewing.item_id)}
                data-testid="vault-view-delete"
              >
                <Trash2 className="w-4 h-4" /> {t("delete")}
              </button>
              <button
                className="btn-gold"
                onClick={() => openEdit(viewing)}
                data-testid="vault-view-edit"
              >
                <Pencil className="w-4 h-4" /> {t("edit")}
              </button>
            </div>
          </div>
        )}
      </Modal>

      {/* CREATE/EDIT modal */}
      <Modal
        open={open}
        onClose={() => {
          setOpen(false);
          setEditingId(null);
        }}
        title={editingId ? t("edit") : t("vault_add")}
        testid="vault-modal"
      >
        <form
          onSubmit={save}
          className="space-y-4"
          autoComplete="off"
          data-form-type="other"
          data-lpignore="true"
          data-1p-ignore="true"
          data-bwignore="true"
        >
          <div>
            <label className="font-label text-faded block mb-2">{t("vault_category_label")}</label>
            <select
              className="input-field"
              value={form.category}
              onChange={(e) => setForm({ ...form, category: e.target.value })}
              data-testid="vault-category"
              autoComplete="off"
            >
              <option value="password">{t("cat_password")}</option>
              <option value="document">{t("cat_document")}</option>
              <option value="photo">{t("cat_photo")}</option>
              <option value="crypto">{t("cat_crypto")}</option>
              <option value="asset">{t("cat_asset")}</option>
            </select>
          </div>
          <div>
            <label className="font-label text-faded block mb-2">{t("title")}</label>
            <input
              className="input-field"
              required
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              data-testid="vault-title"
              autoComplete="off"
              data-lpignore="true"
              data-1p-ignore="true"
              data-bwignore="true"
              name={`vault-title-${vaultFieldKey}`}
            />
          </div>
          {form.category === "password" && (
            <div>
              <label className="font-label text-faded block mb-2">
                {t("username")} <span className="text-faded">({t("optional")})</span>
              </label>
              <input
                className="input-field"
                value={form.username}
                onChange={(e) => setForm({ ...form, username: e.target.value })}
                data-testid="vault-username"
                autoComplete="off"
                data-lpignore="true"
                data-1p-ignore="true"
                data-bwignore="true"
                name={`vault-user-${vaultFieldKey}`}
              />
            </div>
          )}
          {(form.category === "photo" || form.category === "document" || form.category === "asset") && (
            <MediaInput
              textLabel={t("content")}
              textValue={form.content}
              onTextChange={(v) => setForm({ ...form, content: v })}
              media={form}
              onMediaChange={(m) => setForm({ ...form, ...m })}
            />
          )}
          {(form.category === "password" || form.category === "crypto") && (
            <div>
              <label className="font-label text-faded block mb-2">
                {form.category === "password" ? t("vault_field_password") : t("content")}
              </label>
              {form.category === "password" ? (
                <div className="flex items-center gap-2">
                  <input
                    type={reveal ? "text" : "password"}
                    className="input-field flex-1 font-mono"
                    required
                    value={form.content}
                    onChange={(e) => setForm({ ...form, content: e.target.value })}
                    data-testid="vault-content"
                    autoComplete="new-password"
                    data-lpignore="true"
                    data-1p-ignore="true"
                    data-bwignore="true"
                    name={`vault-secret-${vaultFieldKey}`}
                  />
                  <button
                    type="button"
                    onClick={() => setReveal((v) => !v)}
                    className="btn-ghost px-3"
                    data-testid="vault-content-reveal"
                  >
                    {reveal ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              ) : (
                <textarea
                  className="input-field"
                  required
                  value={form.content}
                  onChange={(e) => setForm({ ...form, content: e.target.value })}
                  data-testid="vault-content"
                  autoComplete="off"
                  data-lpignore="true"
                  data-1p-ignore="true"
                  data-bwignore="true"
                  name={`vault-blob-${vaultFieldKey}`}
                />
              )}
            </div>
          )}
          {form.category === "password" && (
            <div>
              <label className="font-label text-faded block mb-2">
                {t("url")} <span className="text-faded">({t("optional")})</span>
              </label>
              <input
                className="input-field"
                value={form.url}
                onChange={(e) => setForm({ ...form, url: e.target.value })}
                placeholder="https://"
                data-testid="vault-url"
                autoComplete="off"
                data-lpignore="true"
                data-1p-ignore="true"
                data-bwignore="true"
                name={`vault-url-${vaultFieldKey}`}
              />
            </div>
          )}
          <div>
            <label className="font-label text-faded block mb-2">
              {t("notes")} <span className="text-faded">({t("optional")})</span>
            </label>
            <input
              className="input-field"
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              autoComplete="off"
              data-lpignore="true"
              data-1p-ignore="true"
              data-bwignore="true"
              name={`vault-notes-${vaultFieldKey}`}
            />
          </div>

          <div className="divider-gold" />
          <label className="flex items-start gap-3 cursor-pointer bg-gold/[0.05] border border-gold-subtle rounded-md p-3 hover:border-gold transition">
            <input
              type="checkbox"
              checked={form.is_capsule}
              onChange={(e) => setForm({ ...form, is_capsule: e.target.checked })}
              className="mt-1 accent-yellow-600"
              data-testid="vault-is-capsule"
            />
            <div className="text-sm">
              <div className="text-gold-light flex items-center gap-2">
                <Bomb className="w-3.5 h-3.5 text-gold" /> {t("vault_capsule_label")}
              </div>
              <div className="text-xs text-muted mt-0.5">
                {t("vault_capsule_desc")}
              </div>
            </div>
          </label>
          <HeirAssignmentPicker
            value={form.assigned_heirs}
            onChange={(arr) => setForm({ ...form, assigned_heirs: arr })}
          />
          <div className="modal-actions">
            <button type="button" className="btn-ghost" onClick={() => setOpen(false)}>
              {t("cancel")}
            </button>
            <button type="submit" className="btn-gold" data-testid="vault-save">
              {t("save")}
            </button>
          </div>
        </form>
      </Modal>
    </div>
    </VaultLock>
  );
}

function DetailField({ label, value, onCopy }) {
  return (
    <div>
      <div className="font-label text-faded mb-2 ks-readonly-label">
        <Lock className="w-3 h-3 opacity-60" /> {label}
      </div>
      <div className="flex items-center gap-2">
        <div className="ks-readonly flex-1 select-text" data-testid="vault-detail-field">
          {value}
        </div>
        <button onClick={onCopy} className="btn-ghost px-3" type="button" aria-label="Copy">
          <Copy className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}
